<template>
  <div class="walk-page">
    <!-- === FIXED OVERLAYS (outside flex layout) === -->

    <!-- Toolbar at top (settings mode only) -->
    <div v-if="!hasActiveSession && !store.isLoading" class="walk-toolbar-fixed">
      <div class="walk-toolbar" :class="{ editing: !!expandedCta }">
        <!-- Progress toggle (left) -->
        <button
          class="toolbar-cta toolbar-cta-toggle"
          :class="{ 'is-off': !store.settings.showProgress, collapsed: !!expandedCta }"
          @click.stop="store.updateSettings({ showProgress: !store.settings.showProgress })"
        >
          <span class="toolbar-icon-dots toolbar-icon-progress">
            <span class="tid green" />
            <span class="tid green" />
            <span class="tid hollow" />
            <span class="tid red" />
          </span>
          <span v-if="!store.settings.showProgress" class="toolbar-strike" />
        </button>

        <!-- Map traces toggle -->
        <button
          class="toolbar-cta toolbar-cta-toggle"
          :class="{ 'is-off': !store.settings.mapEnabled, collapsed: !!expandedCta }"
          @click.stop="store.updateSettings({ mapEnabled: !store.settings.mapEnabled })"
        >
          <span class="toolbar-trace-badge" />
          <span v-if="!store.settings.mapEnabled" class="toolbar-strike" />
        </button>

        <button
          class="toolbar-cta toolbar-cta-toggle"
          :class="{ 'is-squares-on': squaresInspect, collapsed: !!expandedCta }"
          @click.stop="toggleSquaresInspect"
        >
          <span class="toolbar-squares-icon"><span class="toolbar-squares-dot" /></span>
        </button>

        <button class="toolbar-cta toolbar-cta-toggle" :class="{ collapsed: !!expandedCta }" @click.stop="goToWalks">
          <GamIcon :name="GamIconName.WALK" :size="GamIconSize.SMALL" is-current />
        </button>

        <!-- Break editor stays in the tree so it can come back. While the flag is off the
             session keeps the 15 minute default and this control is not mounted. -->
        <div
          v-if="BREAK_UI_ENABLED"
          class="toolbar-cta-wrapper"
          :class="{ expanded: expandedCta === 'break', collapsed: expandedCta && expandedCta !== 'break' }"
        >
          <button
            v-if="expandedCta === 'break'"
            class="toolbar-expand-btn"
            :disabled="editBreak <= 5"
            @click.stop="adjustBreak(-5)"
          >
            −
          </button>
          <button class="toolbar-cta" :class="{ active: expandedCta === 'break' }" @click.stop="toggleExpand('break')">
            <span class="toolbar-cta-text">{{ displayBreakValue }}<span class="toolbar-cta-unit">min</span></span>
          </button>
          <button
            v-if="expandedCta === 'break'"
            class="toolbar-expand-btn"
            :disabled="editBreak >= 60"
            @click.stop="adjustBreak(5)"
          >
            +
          </button>
          <span v-if="expandedCta === 'break'" class="toolbar-cta-label">break</span>
        </div>

        <!-- Route toggle stays for the same reason. Off means the spots are never mounted. -->
        <button
          v-if="ROUTE_UI_ENABLED"
          class="toolbar-cta toolbar-cta-toggle"
          :class="{
            active: showRoute,
            collapsed: !!expandedCta,
            'is-off': !store.settings.mapEnabled,
          }"
          :disabled="!store.settings.mapEnabled"
          @click.stop="showRoute = !showRoute"
        >
          <GamIcon :name="GamIconName.ROUTE" :size="GamIconSize.SMALL" is-current class="toolbar-route-icon" />
        </button>
      </div>
      <div v-if="!expandedCta" class="toolbar-labels">
        <span class="toolbar-label">live result</span>
        <span class="toolbar-label">traces</span>
        <span class="toolbar-label">squares</span>
        <span class="toolbar-label">walks</span>
        <span v-if="BREAK_UI_ENABLED" class="toolbar-label">break</span>
        <span v-if="ROUTE_UI_ENABLED" class="toolbar-label">route</span>
      </div>
    </div>

    <!-- Game info: fixed at top during active session (hidden during walk-out reds) -->
    <div v-if="hasActiveSession && !store.isLoading && !store.isWalkingReds" class="walk-game-info">
      <div class="active-field-row">
        <div class="active-field-chip">{{ store.settings.band.toUpperCase() }}{{ store.settings.speed }}</div>
        <div class="active-field-chip">{{ displaySpeedKmh }}km/h</div>
      </div>
      <template v-if="store.settings.infoVisible">
        <div class="speed-display" :class="{ active: !isIdle }">
          <span class="speed-value" :class="speedColorClass">{{ formattedSpeed }}</span>
        </div>
        <div class="walk-timer-info">
          <div class="timer-row" :class="{ active: isBreak }">
            <span :class="['turn-dot', isBreak ? 'pink' : 'inactive']" />
            <span class="timer-label">{{ localize('walk.timer.break') }}</span>
            <span class="timer-value">{{ formattedBreakTime }}</span>
          </div>
          <div class="timer-row" :class="{ active: isThinking }">
            <span :class="['turn-dot', isThinking ? (sideToMove === 'w' ? 'white' : 'black') : 'inactive']" />
            <span class="timer-label">{{ localize('walk.timer.puzzle') }}</span>
            <span class="timer-value">{{ formattedPuzzleTime }}</span>
          </div>
        </div>
      </template>
    </div>

    <!-- === FLEX LAYOUT (3 children, always stable) === -->
    <div class="WalkView">
      <!-- 1. Top spacer: empty fixed-height row for layout stability -->
      <div class="zone-top-content"></div>

      <!-- 2. Board group -->
      <div class="zone-board-group">
        <div class="zone-board">
          <WalkPuzzle
            ref="puzzleRef"
            :puzzle-data="activePuzzleData"
            :is-submitting="isSubmitting"
            :is-break="isBreak"
            :is-idle="isIdle"
            :is-warmup="store.isWarmup"
            :warmup-progress="store.warmupProgress"
            :warmup-ready="store.warmupReady"
            :break-needs-warmup="store.breakNeedsWarmup"
            :break-warmup-ready="store.breakWarmupReady"
            :current-speed-kmh="store.currentSpeedMps * 3.6"
            :target-speed-kmh="
              store.session?.breakEvenSpeedMps ? store.session.breakEvenSpeedMps * 3.6 : store.settings.speed
            "
            :is-settings-mode="!hasActiveSession"
            :is-loading="store.isLoading"
            :selected-field="{ speed: store.settings.speed, band: store.settings.band }"
            :round-log="store.roundLog"
            :show-progress="store.settings.showProgress"
            :needs-location-update="needsLocationUpdate"
            :submit-failed="submitFailed"
            :submit-retrying="submitRetrying"
            :reveal-failed="revealFailed"
            :end-state="store.boardEndState"
            :timer-fraction="timerFraction"
            :show-reds-overlay="store.isRevealed && store.hasWrongRounds"
            :is-walking-reds="store.isWalkingReds"
            :zoom-level="boardZoomLevel"
            :squares-inspect="squaresInspect"
            @move="handlePuzzleMove"
            @break-tap="handleBreakTap"
            @warmup-tap="handleWarmupTap"
            @walk-reds="handleWalkReds"
            @start="handleStart"
            @retry-location="handleRetryLocation"
            @retry-submit="handleRetrySubmit"
            @reveal-dismiss="handleRevealDismiss"
            @field-select="handleFieldSelect"
            @zoom-select="handleZoomSelect"
          />
          <Transition name="fade">
            <div v-if="isRevealing" class="reveal-loading-overlay">
              <div class="reveal-spinner" />
            </div>
          </Transition>
        </div>
        <div class="board-status-row">
          <span v-if="!hasActiveSession && squaresInspect" class="squares-solved-legend">
            <i class="squares-solved-swatch" />
            {{ localize('walk.squares.legend.solved') }}
          </span>
          <span v-else-if="!hasActiveSession" class="status-settings">
            Walk <span class="status-field">{{ store.settings.band.toUpperCase() }}{{ store.settings.speed }}</span> ·
            {{ selectedBandLabel }} · {{ store.settings.speed }}km/h
          </span>
          <span v-else-if="showNoSignal" class="status-reds">{{ localize('location.no_gps_signal') }}</span>
          <GamWalkPrompt v-else-if="store.isWarmup && !store.warmupReady" />
          <span v-else-if="store.isWarmup && store.warmupReady">Tap the Board</span>
          <!-- prettier-ignore -->
          <GamWalkPrompt v-else-if="isBreak && store.breakNeedsWarmup && !store.breakWarmupReady && !store.isWalkingReds" />
          <!-- prettier-ignore -->
          <span v-else-if="isBreak && store.breakNeedsWarmup && store.breakWarmupReady && !store.isWalkingReds">Tap the Board</span>
          <span v-else-if="isBreak && !store.isWalkingReds" class="status-puzzle-info">
            Tap the Board
            <span class="status-sep">·</span>
            <img :src="puzzleIcon" class="status-puzzle-icon" alt="" />
            {{ store.session?.currentRound ?? 0 }}
          </span>
          <span v-else-if="isBreak && store.isWalkingReds" class="status-puzzle-info">
            Tap the Board
            <span class="status-sep">·</span>
            {{ store.redWalkProgress }}/{{ store.wrongCount }}
          </span>
          <span v-else-if="isThinking && !store.isWalkingReds" class="status-puzzle-info">
            <span :class="['status-turn-dot', sideToMove === 'w' ? 'white' : 'black']" />
            {{ sideToMove === 'w' ? 'White to Move' : 'Black to Move' }}
            <span class="status-sep">·</span>
            <img :src="puzzleIcon" class="status-puzzle-icon" alt="" />
            {{ store.session?.currentRound ?? 0 }}
          </span>
          <span v-else-if="store.isWalkingReds" class="status-puzzle-info status-reds">
            <span :class="['status-turn-dot', sideToMove === 'w' ? 'white' : 'black']" />
            {{ sideToMove === 'w' ? 'White to Move' : 'Black to Move' }}
            <span class="status-sep">·</span>
            {{ isThinking ? store.redWalkProgress + 1 : store.redWalkProgress }}/{{ store.wrongCount }}
          </span>
        </div>
      </div>

      <!-- Start / Finish / Reveal CTAs -->
      <div v-if="!hasActiveSession" class="walk-action-row walk-start-zoom-row">
        <!-- The zoom glyphs share the space the road runs through, so they step
             aside while the route is up. -->
        <button
          v-if="!showsSetupRoute"
          type="button"
          class="walk-zoom-glyph"
          :class="{ disabled: boardZoomLevel >= 8 }"
          :disabled="boardZoomLevel >= 8 || store.isLoading"
          aria-label="Zoom out"
          @click="zoomBoardOut"
        >
          <span class="walk-zoom-glyph-icon" aria-hidden="true">−</span>
        </button>
        <div v-if="ROUTE_UI_ENABLED && showsSetupRoute" class="walk-route-layer">
          <WalkRouteSpot
            role="start"
            :spot="effectiveStartSpot"
            :disabled="store.isLoading"
            :nearby="isStartSpotNearby"
            :can-remove="canEditSpots"
            :distance-label="startDistanceLabel"
            @select="!store.isLoading && handleStartSpot()"
            @remove="store.setSpot('start', null)"
          />
          <span class="spot-connector" />
          <WalkRouteSpot
            role="reveal"
            :spot="effectiveRevealSpot"
            :disabled="store.isLoading"
            :can-remove="canEditSpots"
            @select="!store.isLoading && handleRevealSpotSelect()"
            @remove="store.setSpot('reveal', null)"
          />
        </div>
        <button
          class="walk-action-cta walk-action-start"
          :class="{ disabled: store.isLoading }"
          :disabled="store.isLoading"
          @click="handleStart"
        >
          Start
        </button>
        <button
          v-if="!showsSetupRoute"
          type="button"
          class="walk-zoom-glyph"
          :class="{ disabled: boardZoomLevel <= 1 }"
          :disabled="boardZoomLevel <= 1 || store.isLoading"
          aria-label="Zoom in"
          @click="zoomBoardIn"
        >
          <span class="walk-zoom-glyph-icon" aria-hidden="true">+</span>
        </button>
      </div>
      <button
        v-else-if="store.isRevealed && store.hasWrongRounds"
        class="walk-action-cta walk-action-reveal"
        @click="handleBoardFinish"
      >
        Finish
      </button>
      <div v-else class="walk-action-row">
        <!-- The route sits behind the CTA so the road runs through it without
             moving the board or the button off their session positions. -->
        <div v-if="ROUTE_UI_ENABLED && showsSessionRoute" class="walk-route-layer is-static">
          <WalkRouteSpot role="start" :spot="effectiveStartSpot" disabled />
          <span class="spot-connector" />
          <WalkRouteSpot
            role="reveal"
            :spot="effectiveRevealSpot"
            disabled
            :nearby="isRevealSpotNearby"
            :distance-label="revealDistanceLabel"
            :total-time="isIdle ? null : formattedTotalTime"
          />
        </div>
        <button
          class="walk-action-cta walk-action-reveal"
          :class="{ disabled: store.isLoading || isRevealing }"
          @click="handleRevealSpot"
        >
          {{ store.isWarmup ? 'Cancel' : 'Reveal' }}
        </button>
        <button v-if="showForceQuit" class="walk-action-cta walk-force-quit" @click="handleForceQuit">
          Force<br />quit
        </button>
      </div>
    </div>

    <!-- === FIXED BOTTOM OVERLAYS === -->
    <!-- A running session keeps only the map button: Force quit is the way out of
         a walk, so the close button does not appear while it lasts. The map
         button takes the close button's size and sits on the opposite corner. -->
    <div v-if="!hasActiveSession" class="walk-close-btn" :class="{ disabled: store.isLoading }" @click="handleClose">
      <GamIcon :name="GamIconName.X_CLOSE" :size="GamIconSize.NORMAL" is-current />
    </div>
    <button v-else-if="!isOfflineSession" class="walk-close-btn walk-map-btn" @click="goToMap">
      <GamIcon :name="GamIconName.MAP" :size="GamIconSize.NORMAL" is-current />
    </button>
    <WalkReveal
      :is-open="showReveal"
      :walk-duration-ms="walkDurationMs"
      @close="handleRevealClose"
      @walk-reds="handleWalkReds"
      @finish="handleRevealFinish"
    />

    <WalkFieldDrawer :field-key="drawerFieldKey" @close="drawerFieldKey = ''" @walk="finishFieldInspect" />

    <WalkRedResult
      v-if="showRedWalkResult"
      :corrected="redWalkCorrected"
      :total="redWalkTotal"
      :dots="redWalkDots"
      @close="handleRedWalkResultClose"
      @view-map="handleRedWalkViewMap"
    />

    <GamConfirmModal
      v-if="proximityModal"
      :title="localize('walk.proximity.title')"
      :keep-label="
        proximityModal === 'start' ? localize('walk.proximity.start_keep') : localize('walk.proximity.reveal_keep')
      "
      :destructive-label="
        proximityModal === 'start' ? localize('walk.proximity.start_anyway') : localize('walk.proximity.reveal_anyway')
      "
      neutral-confirm
      @close="proximityModal = null"
      @keep="proximityModal = null"
      @confirm="handleProximityAnyway"
    />

    <GamConfirmModal
      v-if="endEarlyModal"
      :title="store.isWarmup ? localize('walk.cancel.title') : localize('walk.end_early.title')"
      :keep-label="localize('walk.end_early.keep')"
      :destructive-label="store.isWarmup ? localize('walk.cancel.confirm') : localize('walk.end_early.confirm')"
      @close="endEarlyModal = false"
      @keep="endEarlyModal = false"
      @confirm="handleEndEarly"
    />
  </div>
</template>

<script lang="ts" setup>
import { RouterNameType } from '@/core/data/config/uiConfig.interface';
import { localize } from '@/core/gambits';
import { formatDistance, haversineDistance } from '@/core/helpers/location.helper';
import { platform } from '@/core/platform';
import { useAuthStore } from '@/stores/auth.store';
import { useGeolocationStore } from '@/stores/geolocation.store';
import { useMapStore } from '@/stores/map.store';
import { useWalkStore } from '@/stores/walk.store';
import GamConfirmModal from '@/views/components/GamConfirmModal.vue';
import GamIcon from '@/views/components/GamIcon.vue';
import GamWalkPrompt from '@/views/components/GamWalkPrompt.vue';
import { GamIconName, GamIconSize } from '@/views/composables/constants/components/gamIcon.constants';
import { useGhostClickGuard } from '@/views/composables/hooks/useGhostClickGuard';
import { rememberLocationFunction, requestLocationFor } from '@/views/composables/hooks/useLocationAsk';
import { notifyLocationDenied, notifyLocationUnavailable } from '@/views/composables/hooks/useLocationHelp';
import type { PuzzleData } from '@/views/composables/hooks/usePuzzleEngine';
import { useSpotProximity } from '@/views/composables/hooks/useSpotProximity';
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useToast } from 'vue-toastification';
import type { WalkZoomLevel } from './components/WalkPuzzle.vue';
import WalkPuzzle from './components/WalkPuzzle.vue';
import WalkRouteSpot from './components/WalkRouteSpot.vue';
import WalkFieldDrawer from './modals/WalkFieldDrawer.vue';
import WalkRedResult from './modals/WalkRedResult.vue';
import WalkReveal from './modals/WalkReveal.vue';

/** Off until the break control returns. Sessions stay on the 15 minute default. */
const BREAK_UI_ENABLED = false;
const DEFAULT_BREAK_MS = 900_000;
/** Off until the route control returns. Saved spots stay stored; the layer stays unmounted. */
const ROUTE_UI_ENABLED = false;

const ZOOM_LEVELS: WalkZoomLevel[] = [8, 4, 2, 1];
const boardZoomLevel = ref<WalkZoomLevel>(8);

const puzzleIcon = new URL('../../../ui/icons/puzzle-grid.svg', import.meta.url).href;

const router = useRouter();
const store = useWalkStore();
if (!BREAK_UI_ENABLED && !store.hasActiveSession && store.settings.breakBankMs !== DEFAULT_BREAK_MS) {
  store.updateSettings({ breakBankMs: DEFAULT_BREAK_MS });
}
const mapStore = useMapStore();
const geolocationStore = useGeolocationStore();
const authStore = useAuthStore();

function zoomBoardIn() {
  const idx = ZOOM_LEVELS.indexOf(boardZoomLevel.value);
  if (idx < ZOOM_LEVELS.length - 1) {
    boardZoomLevel.value = ZOOM_LEVELS[idx + 1];
  }
}

function zoomBoardOut() {
  const idx = ZOOM_LEVELS.indexOf(boardZoomLevel.value);
  if (idx > 0) {
    boardZoomLevel.value = ZOOM_LEVELS[idx - 1];
  }
}

function handleZoomSelect(field: { speed: number; band: string }) {
  store.updateSettings({
    speed: field.speed,
    band: field.band,
    breakEvenSpeedMps: field.speed / 3.6,
  });
}
const { absorb: absorbGhostClick } = useGhostClickGuard();

const handleClose = () => {
  absorbGhostClick();
  showRoute.value = false;
  // Reached from the map there is something to go back to; reached from a link
  // there is nothing behind it, and back would leave the app altogether.
  if (window.history.state?.back) {
    router.back();
    return;
  }
  router.replace({ name: RouterNameType.LOCATIONS });
};

const goToWalks = () => {
  absorbGhostClick();
  showRoute.value = false;
  router.push({ name: RouterNameType.WALKS, query: { back: 'history' } });
};

const squaresInspect = ref(false);
const drawerFieldKey = ref('');
let lastSquaresToggleAt = 0;

const toggleSquaresInspect = () => {
  const now = Date.now();
  if (now - lastSquaresToggleAt < 400) return;
  lastSquaresToggleAt = now;
  squaresInspect.value = !squaresInspect.value;
  if (!squaresInspect.value) drawerFieldKey.value = '';
};

function finishFieldInspect() {
  squaresInspect.value = false;
  drawerFieldKey.value = '';
}

const NEARBY_THRESHOLD_M = 50;

const effectiveStartSpot = computed(() => store.sessionStartSpot ?? store.startSpot);
const effectiveRevealSpot = computed(() => store.sessionRevealSpot ?? store.revealSpot);

const puzzleRef = ref<InstanceType<typeof WalkPuzzle>>();
const showReveal = ref(false);
const showRoute = ref(false);
const isSubmitting = ref(false);
const proximityModal = ref<'start' | 'reveal' | null>(null);

// --- Toolbar CTA expansion ---
const expandedCta = ref<'break' | null>(null);
const editBreak = ref(Math.round(store.settings.breakBankMs / 60_000));

const displayBreakMin = computed(() => Math.round(store.settings.breakBankMs / 60_000));
const displayBreakValue = computed(() => (expandedCta.value === 'break' ? editBreak.value : displayBreakMin.value));

function toggleExpand(cta: 'break') {
  if (expandedCta.value === cta) {
    collapseToolbar();
  } else {
    expandedCta.value = cta;
    editBreak.value = Math.round(store.settings.breakBankMs / 60_000);
  }
}

function collapseToolbar() {
  if (expandedCta.value === 'break') {
    store.updateSettings({ breakBankMs: editBreak.value * 60_000 });
  }
  expandedCta.value = null;
}

function adjustBreak(delta: number) {
  editBreak.value = Math.max(5, Math.min(60, editBreak.value + delta));
}

function handleToolbarClickOutside(e: PointerEvent) {
  if (!expandedCta.value) return;
  const target = e.target as HTMLElement;
  if (target.closest('.toolbar-cta-wrapper.expanded')) return;
  collapseToolbar();
}

if (BREAK_UI_ENABLED && typeof document !== 'undefined') {
  document.addEventListener('pointerdown', handleToolbarClickOutside);
}

onBeforeUnmount(() => {
  if (typeof document !== 'undefined') {
    document.removeEventListener('pointerdown', handleToolbarClickOutside);
  }
});

const hasActiveSession = computed(() => store.hasActiveSession);
const isBreak = computed(() => store.isBreak);
const isIdle = computed(() => store.isIdle);

// Without a fix the speed reads zero however fast you walk, so every prompt below
// the board would be asking for something the session cannot see you do.
const showNoSignal = computed(() => hasActiveSession.value && !store.hasGpsSignal);

watch(hasActiveSession, (active) => {
  if (active) {
    boardZoomLevel.value = 8;
    squaresInspect.value = false;
    drawerFieldKey.value = '';
  }
});
const isThinking = computed(() => store.isThinking);

const hasSpots = computed(() => !!effectiveStartSpot.value || !!effectiveRevealSpot.value);
const canEditSpots = computed(() => !hasActiveSession.value && !store.isLoading);

/**
 * Only one end of the route is a destination at a time: the start spot gates
 * Start before a session, the reveal spot gates Reveal during one.
 */
const proximityTargetSpot = computed(() => {
  if (!ROUTE_UI_ENABLED || store.isLoading || store.isWalkingReds) return null;
  return hasActiveSession.value ? effectiveRevealSpot.value : effectiveStartSpot.value;
});
const proximityTargetCoords = computed(() => proximityTargetSpot.value?.coordinates ?? null);
const { distance: targetDistance, isNearby: isTargetNearby } = useSpotProximity(proximityTargetCoords);

const isStartSpotNearby = computed(
  () => !hasActiveSession.value && !!proximityTargetSpot.value && isTargetNearby.value,
);
const isRevealSpotNearby = computed(
  () => hasActiveSession.value && !!proximityTargetSpot.value && isTargetNearby.value,
);

const targetDistanceLabel = computed(() => {
  if (!proximityTargetSpot.value || isTargetNearby.value) return null;
  const distance = targetDistance.value;
  if (distance === null) return null;
  if (distance >= 100_000) return localize('spot.distance.far_away');
  return formatDistance(distance, authStore.unitType).join('');
});

const startDistanceLabel = computed(() => (hasActiveSession.value ? null : targetDistanceLabel.value));
const revealDistanceLabel = computed(() => (hasActiveSession.value ? targetDistanceLabel.value : null));

const showsSetupRoute = computed(() => spotsVisible.value && !hasActiveSession.value);

/**
 * Once revealed there is nowhere left to walk, and the reds lap has no
 * destination at all, so the route leaves with the reveal.
 */
const showsSessionRoute = computed(
  () => spotsVisible.value && hasActiveSession.value && !store.isRevealed && !store.isWalkingReds,
);
const isOfflineSession = computed(() => platform.isOfflineCapable && !navigator.onLine);
const spotsVisible = computed(() => {
  if (!ROUTE_UI_ENABLED) return false;
  if (showRoute.value) return true;
  if (hasSpots.value && hasActiveSession.value) return true;
  return false;
});

watch(
  () => store.settings.mapEnabled,
  (enabled) => {
    if (!enabled) {
      showRoute.value = false;
      store.setSpot('start', null);
      store.setSpot('reveal', null);
    }
  },
);

const formattedSpeed = computed(() => {
  return `${(store.currentSpeedMps * 3.6).toFixed(1)}km/h`;
});

const speedColorClass = computed(() => {
  if (!isThinking.value) return '';
  const target = store.session?.breakEvenSpeedMps ?? store.settings.breakEvenSpeedMps;
  return store.currentSpeedMps >= target ? 'speed-ok' : 'speed-low';
});

const displaySpeedKmh = computed(() => {
  const mps = store.session?.breakEvenSpeedMps ?? store.settings.breakEvenSpeedMps;
  return Math.round(mps * 3.6);
});

const timerFraction = computed(() => {
  if (!store.session) return 1;
  if (store.session.currentPhase === 'warmup') {
    return store.warmupProgress;
  }
  if (store.session.currentPhase === 'break') {
    if (store.breakNeedsWarmup) {
      return store.warmupProgress;
    }
    const totalBreakMs = store.settings.breakBankMs;
    if (totalBreakMs <= 0) return 1;
    return Math.max(0, store.breakTimeMs / totalBreakMs);
  }
  if (!store.session.timeBankMs) return 1;
  return Math.max(0, Math.min(1, store.localTimeMs / store.session.timeBankMs));
});

const formattedBreakTime = computed(() => {
  const ms = store.breakTimeMs;
  const totalSec = Math.max(0, Math.floor(ms / 1000));
  const min = Math.floor(totalSec / 60);
  const sec = totalSec % 60;
  return `${min}:${String(sec).padStart(2, '0')}`;
});

const formattedPuzzleTime = computed(() => {
  const ms = store.isBreak ? (store.session?.timeBankMs ?? 0) : store.localTimeMs;
  const sec = Math.max(0, ms / 1000);
  return sec.toFixed(1);
});

// --- Stopwatch: total session time (starts on warmup tap, stops on reveal) ---
const STOPWATCH_KEY = 'walk_stopwatch_start';
const totalTimeDisplay = ref('0:00');
let stopwatchStart = 0;
let stopwatchInterval: ReturnType<typeof setInterval> | null = null;

function formatStopwatch(ms: number): string {
  const totalSec = Math.max(0, Math.round(ms / 1000));
  const min = Math.floor(totalSec / 60);
  const sec = totalSec % 60;
  return `${min}:${String(sec).padStart(2, '0')}`;
}

function startStopwatch() {
  stopwatchStart = Date.now();
  try {
    localStorage.setItem(STOPWATCH_KEY, String(stopwatchStart));
  } catch {}
  stopStopwatch();
  stopwatchInterval = setInterval(() => {
    totalTimeDisplay.value = formatStopwatch(Date.now() - stopwatchStart);
  }, 1000);
}

function resumeStopwatch() {
  const persisted = localStorage.getItem(STOPWATCH_KEY);
  if (persisted) {
    stopwatchStart = Number(persisted);
  } else {
    const log = store.roundLog;
    if (!log.length) return;
    stopwatchStart = log[0].timestamp;
  }
  stopStopwatch();
  totalTimeDisplay.value = formatStopwatch(Date.now() - stopwatchStart);
  stopwatchInterval = setInterval(() => {
    totalTimeDisplay.value = formatStopwatch(Date.now() - stopwatchStart);
  }, 1000);
}

function stopStopwatch() {
  if (stopwatchInterval != null) {
    clearInterval(stopwatchInterval);
    stopwatchInterval = null;
  }
  try {
    localStorage.removeItem(STOPWATCH_KEY);
  } catch {}
}

onBeforeUnmount(() => {
  if (stopwatchInterval != null) {
    clearInterval(stopwatchInterval);
    stopwatchInterval = null;
  }
});

const formattedTotalTime = computed(() => totalTimeDisplay.value);

const sideToMove = computed(() => {
  const fen = store.currentPuzzle?.fen;
  if (!fen) return 'w';
  const fenTurn = fen.split(' ')[1];
  return fenTurn === 'w' ? 'b' : 'w';
});

const RATING_BANDS: Record<string, { min: number; max: number; label: string }> = {
  a: { min: 400, max: 599, label: '400–599' },
  b: { min: 600, max: 799, label: '600–799' },
  c: { min: 800, max: 999, label: '800–999' },
  d: { min: 1000, max: 1299, label: '1000–1299' },
  e: { min: 1300, max: 1599, label: '1300–1599' },
  f: { min: 1600, max: 1899, label: '1600–1899' },
  g: { min: 1900, max: 2199, label: '1900–2199' },
  h: { min: 2200, max: 2500, label: '2200–2500' },
};

const selectedBandLabel = computed(() => {
  const band = store.settings.band;
  return RATING_BANDS[band]?.label ?? band;
});

function handleFieldSelect(field: { speed: number; band: string }) {
  store.updateSettings({
    speed: field.speed,
    band: field.band,
    breakEvenSpeedMps: field.speed / 3.6,
  });

  if (squaresInspect.value) {
    drawerFieldKey.value = `${field.speed}${field.band}`;
  }
}

const activePuzzleData = computed<PuzzleData | null>(() => {
  if (!store.currentPuzzle) return null;
  return {
    id: store.currentPuzzle.roundNumber,
    fen: store.currentPuzzle.fen,
    setupMove: store.currentPuzzle.setupMove,
    solutionMove: '',
    rating: store.currentPuzzle.rating,
    skipSetupAnimation: true,
  };
});

onMounted(async () => {
  if (geolocationStore.isLocationPermissionGranted) {
    geolocationStore.requestFreshPosition();
  }
  store.loadStreakRecords();
  store.loadFieldStats();

  if (ROUTE_UI_ENABLED && !store.hasActiveSession && (store.startSpot || store.revealSpot)) {
    showRoute.value = true;
  }

  if (store.hasActiveSession && !store.isRevealed && !store.isWalkingReds) {
    store.flushSyncQueue();
    resumeStopwatch();

    if (store.sessionEndState) {
      doReveal();
    }
    return;
  }

  if (store.isWalkingReds) return;

  if (store.isRevealed) {
    if (store.showRevealOnReturn && store.lastSummary) {
      store.showRevealOnReturn = false;
      if (!store.hasWrongRounds) {
        await store.finishSession();
      }
      showReveal.value = true;
    }
    return;
  }

  await store.loadActiveSession();
  if (store.hasActiveSession && !store.isRevealed && !store.sessionEndState) {
    resumeStopwatch();
  }
  if (store.isRevealed && !store.hasWrongRounds) {
    await store.finishSession();
  }
});

function notifyLocationDeniedAndReset() {
  puzzleRef.value?.resetLoading();
  notifyLocationDenied('walk');
}

async function handleStart() {
  if (store.hasActiveSession || store.isLoading) return;

  if (!geolocationStore.isLocationPermissionGranted) {
    puzzleRef.value?.resetLoading();
    requestLocationFor('walk', () => {
      void handleStart();
    });
    return;
  }

  const spot = store.startSpot;
  if (spot?.coordinates) {
    const userLoc = await store.getProximityLocation();
    // The proximity lookup can surface a first-time deny.
    if (geolocationStore.isLocationPermissionDenied) {
      notifyLocationDeniedAndReset();
      return;
    }
    if (!userLoc || haversineDistance(spot.coordinates, userLoc) > NEARBY_THRESHOLD_M) {
      puzzleRef.value?.resetLoading();
      proximityModal.value = 'start';
      return;
    }
  }

  const result = await store.createSession(spot?.id);
  if (result.success) {
    rememberLocationFunction();
    startStopwatch();
    return;
  }
  await handleStartFailure(result);
}

type CreateSessionResult = Awaited<ReturnType<typeof store.createSession>>;

async function handleStartFailure(result: CreateSessionResult) {
  if (result.success) return;

  if (result.reason === 'no_location') {
    handleLocationFailure();
    return;
  }

  puzzleRef.value?.resetLoading();

  // No eligible puzzles left in the selected field — a real, recurring state.
  if (result.errorKey === 'walk_no_puzzles_in_field') {
    useToast().error(localize('walk.error.no_puzzles_in_field'), {
      id: 'walk-start-failed',
      timeout: 6000,
    });
    return;
  }

  if (result.statusCode === 400) {
    useToast().error(result.errorMessage ?? localize('walk.error.move_failed'), {
      id: 'walk-start-failed',
      timeout: 5000,
    });
    await store.loadActiveSession();
    if (store.isRevealed && store.session) {
      await store.loadSummary(store.session.id);
      showReveal.value = true;
    }
    return;
  }

  // No 400 body: a transient failure (deploy rollout 5xx, network drop). Retrying
  // in a moment usually succeeds, so say so instead of a flat error.
  useToast().error(localize('walk.error.try_again_soon'), {
    id: 'walk-start-failed',
    timeout: 6000,
  });
}

async function handleLocationFailure() {
  if (geolocationStore.isLocationPermissionDenied) {
    notifyLocationDeniedAndReset();
    return;
  }

  puzzleRef.value?.resetLoading();

  let permState: PermissionState | null = null;
  try {
    const perm = await navigator.permissions.query({ name: 'geolocation' });
    permState = perm.state;
  } catch {
    // Permissions API unavailable (e.g. iOS Safari)
  }

  if (permState === 'denied') {
    notifyLocationDenied('walk');
  } else {
    notifyLocationUnavailable('walk');
  }
}

async function handleWarmupTap() {
  if (store.isWarmup) {
    await store.startThink();
  }
}

async function handleBreakTap() {
  if (store.isBreak) {
    if (!store.requestStartFromBreak()) return;
    await store.startThink();
  }
}

const pendingMoveUci = ref<string | null>(null);
const needsLocationUpdate = ref(false);
const submitFailed = ref(false);
const submitRetrying = ref(false);

async function handlePuzzleMove(moveUci: string) {
  if (!store.isThinking) return;
  pendingMoveUci.value = moveUci;

  const result = await store.submitMove(moveUci);

  if (result.success) {
    pendingMoveUci.value = null;
    submitFailed.value = false;
    needsLocationUpdate.value = false;
    return;
  }

  if (result.submitFailed) {
    submitRetrying.value = true;
    await new Promise((r) => setTimeout(r, 1500));
    await store.flushSyncQueue();

    if (store.syncQueue.some((e: { type: string }) => e.type === 'submitMove')) {
      submitRetrying.value = false;
      submitFailed.value = true;
      return;
    }

    pendingMoveUci.value = null;
    submitRetrying.value = false;
    submitFailed.value = false;
    store.startTimerLoop();
    return;
  }

  pendingMoveUci.value = null;
}

async function handleRetrySubmit() {
  if (!pendingMoveUci.value) {
    submitFailed.value = false;
    await store.loadActiveSession();
    return;
  }

  submitFailed.value = false;
  submitRetrying.value = true;

  await store.flushSyncQueue();

  if (store.syncQueue.some((e: { type: string }) => e.type === 'submitMove')) {
    submitRetrying.value = false;
    submitFailed.value = true;
    return;
  }

  pendingMoveUci.value = null;
  submitRetrying.value = false;
  store.startTimerLoop();
}

async function handleRetryLocation() {
  if (!pendingMoveUci.value) return;
  needsLocationUpdate.value = false;

  await store.flushSyncQueue();

  if (store.syncQueue.some((e: { type: string }) => e.type === 'submitMove')) {
    submitFailed.value = true;
    return;
  }

  pendingMoveUci.value = null;
}

function handleStartSpot() {
  showRoute.value = false;
  if (hasActiveSession.value) return;
  mapStore.enterProposalMode({ walkSpotRole: 'start' });
  if (store.startSpot) mapStore.setMapProposalLocation(store.startSpot);
  router.push({ name: RouterNameType.LOCATIONS });
}

function handleRevealSpotSelect() {
  showRoute.value = false;
  if (hasActiveSession.value) return;
  mapStore.enterProposalMode({ walkSpotRole: 'reveal' });
  if (store.revealSpot) mapStore.setMapProposalLocation(store.revealSpot);
  router.push({ name: RouterNameType.LOCATIONS });
}

const isRevealing = ref(false);
const revealFailed = ref(false);
const showForceQuit = computed(() => store.hasActiveSession && revealFailed.value);
const walkDurationMs = ref<number | null>(null);
const endEarlyModal = ref(false);
const redWalkAttempted = ref(false);
const showRedWalkResult = ref(false);
const redWalkCorrected = ref(0);
const redWalkTotal = ref(0);
const redWalkDots = ref<boolean[]>([]);
const wasWalkingReds = ref(false);

watch(
  () => store.session?.status,
  (status, oldStatus) => {
    if (status === 'walking_reds' && oldStatus !== 'walking_reds') {
      redWalkAttempted.value = false;
    }
  },
);

watch(
  () => store.currentPuzzle,
  (puzzle) => {
    if (puzzle && store.session?.status === 'walking_reds') {
      redWalkAttempted.value = true;
    }
  },
);

async function handleRevealSpot() {
  if (isRevealing.value) return;
  if (!hasActiveSession.value) {
    mapStore.enterProposalMode({ walkSpotRole: 'reveal' });
    if (store.revealSpot) mapStore.setMapProposalLocation(store.revealSpot);
    router.push({ name: RouterNameType.LOCATIONS });
    return;
  }
  if (store.isWarmup || (store.isWalkingReds && !redWalkAttempted.value)) {
    endEarlyModal.value = true;
    return;
  }
  revealFailed.value = false;
  await doReveal();
}

async function doReveal(skipProximity = false) {
  if (isRevealing.value) return;
  isRevealing.value = true;
  submitFailed.value = false;
  submitRetrying.value = false;
  wasWalkingReds.value = store.isWalkingReds;

  let spot = store.revealSpot;
  if (wasWalkingReds.value) {
    spot = null;
  } else if (!skipProximity && spot?.coordinates) {
    const userLoc = await store.getProximityLocation();
    if (!userLoc || haversineDistance(spot.coordinates, userLoc) > NEARBY_THRESHOLD_M) {
      puzzleRef.value?.resetLoading();
      proximityModal.value = 'reveal';
      isRevealing.value = false;
      return;
    }
  }

  walkDurationMs.value = stopwatchStart > 0 ? Date.now() - stopwatchStart : null;
  stopStopwatch();

  if (skipProximity) {
    store.setSpot('reveal', null);
    spot = null;
  }

  const result = await store.reveal(spot?.id);
  if (result) {
    await store.loadSummary(result.session.id);
    if (wasWalkingReds.value && store.lastSummary) {
      redWalkCorrected.value = store.lastSummary.redWalkCorrected;
      redWalkTotal.value = store.lastSummary.redWalkTotal;
      redWalkDots.value = store.roundLog.map((r) => r.isCorrect === true);
      showRedWalkResult.value = true;
    } else {
      if (!store.hasWrongRounds) {
        await store.finishSession();
      }
      showReveal.value = true;
    }
  } else {
    revealFailed.value = true;
  }

  isRevealing.value = false;
}

async function handleEndEarly() {
  endEarlyModal.value = false;
  if (store.isWarmup && store.session) {
    // Reset first so the UI leaves warmup immediately — awaiting the delete kept the
    // Cancel button live and let a second confirm fire a duplicate delete.
    const sessionId = store.session.id;
    store.reset();
    await store.deleteSession(sessionId);
    return;
  }
  await doReveal();
}

async function handleProximityAnyway() {
  const action = proximityModal.value;
  proximityModal.value = null;

  if (action === 'start') {
    if (geolocationStore.isLocationPermissionDenied) {
      notifyLocationDeniedAndReset();
      return;
    }
    store.setSpot('start', null);
    puzzleRef.value?.setIdleLoading(true);
    const userLoc = geolocationStore.userLocation?.location ?? undefined;
    const result = await store.createSession(undefined, userLoc);
    if (result.success) {
      rememberLocationFunction();
    } else {
      await handleStartFailure(result);
    }
  } else if (action === 'reveal') {
    await doReveal(true);
  }
}

function handleRevealDismiss() {
  revealFailed.value = false;
}

function handleRevealClose() {
  showReveal.value = false;
}

async function handleRevealFinish() {
  showReveal.value = false;
  await store.finishSession();
  store.loadStreakRecords();
  store.loadFieldStats();
}

async function handleBoardFinish() {
  const result = await store.finishSession();
  if (!result) {
    store.reset();
  }
  store.loadStreakRecords();
  store.loadFieldStats();
}

async function handleForceQuit() {
  if (store.session?.status === 'completed') {
    const sessionId = store.session.id;
    store.reset();
    await store.deleteSession(sessionId);
  } else {
    const result = await store.finishSession();
    if (!result) {
      store.reset();
    }
  }
  revealFailed.value = false;
  store.loadStreakRecords();
  store.loadFieldStats();
}

async function handleRedWalkResultClose() {
  showRedWalkResult.value = false;
  if (store.session?.status !== 'finished') {
    await store.finishSession();
  }
  store.loadStreakRecords();
  store.loadFieldStats();
}

async function handleRedWalkViewMap() {
  showRedWalkResult.value = false;
  if (store.session?.status !== 'finished') {
    await store.finishSession();
  }
  store.requestShowWalkMarkers();
  router.replace({ name: RouterNameType.LOCATIONS, query: { fit_markers: 'true' } });
}

async function handleWalkReds() {
  showReveal.value = false;
  const result = await store.walkReds();
  if (!result) {
    useToast().error(localize('walk.walk_reds.error'), { id: 'walk-reds-failed' });
  }
}

watch(
  () => store.sessionEndState,
  (state) => {
    if (state && !showReveal.value && !revealFailed.value && !isRevealing.value) {
      doReveal();
    }
  },
);

function goToMap() {
  router.replace({ name: RouterNameType.LOCATIONS });
}
</script>

<style lang="scss" scoped>
@use '@/ui/css/mixins' as *;
.walk-page {
  height: 100dvh;
  width: 100%;
  background: linear-gradient(175deg, oklch(0.28 0.035 var(--hue)) 0%, var(--color-dark-700) 45%), var(--color-dark-700);
  overflow: hidden;
  position: relative;
}

.WalkView {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-evenly;
  height: 100%;
  width: 100%;
  padding-top: calc(var(--sai-top) + 70px);
  padding-bottom: calc(var(--safe-bottom-offset) + 50px);
}

// --- Top content slot (fixed height to anchor board position) ---

.zone-top-content {
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: visible;
}

// --- Board layout ---

.zone-board-group {
  display: flex;
  flex-direction: column;
  align-items: center;
  min-height: 0;
  width: 100%;
}

.zone-board {
  width: min(100vw - 20px, 100dvh - var(--board-offset, 320px), 560px);
  max-height: min(100vw - 20px, 100dvh - var(--board-offset, 320px), 560px);
  aspect-ratio: 1;
  min-height: 0;
  position: relative;
  z-index: 10;
}

.board-status-row {
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 600;
  color: var(--color-white-60);
  letter-spacing: 0.02em;

  .status-field {
    color: var(--color-pink);
  }

  /* Settings line only. Active-session labels keep the grey above. */
  .status-settings {
    color: var(--color-white);
  }
}

.squares-solved-legend {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  color: var(--color-white-50);
  font-size: 11px;
  font-weight: 600;
  line-height: 1;
  letter-spacing: 0;
}

.squares-solved-swatch {
  flex: none;
  width: 22px;
  height: 10px;
  box-sizing: border-box;
  background: linear-gradient(to right, #000, var(--color-white));
}

.status-puzzle-info {
  display: inline-flex;
  align-items: center;
  gap: 5px;
}

.status-turn-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;

  &.white {
    background: #fff;
  }

  &.black {
    background: #000;
    border: 1px solid rgba(255, 255, 255, 0.3);
  }
}

.status-sep {
  color: var(--color-white-20);
}

.status-puzzle-icon {
  width: 11px;
  height: 11px;
  opacity: 0.7;
}

.status-reds {
  color: var(--color-incorrect);
}

// --- Game mode elements ---

.active-field-row {
  position: absolute;
  top: 0;
  left: 14px;
  right: 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  pointer-events: auto;
}

.active-field-chip {
  font-size: 13px;
  font-weight: 600;
  color: var(--color-white-70);
  white-space: nowrap;
  background: rgba(255, 255, 255, 0.06);
  backdrop-filter: blur(15px);
  border-radius: 1rem;
  padding: 8px 14px;
}

.active-variant-chip {
  font-size: 11px;
  font-weight: 600;
  color: var(--color-white-50);
  white-space: nowrap;
  background: rgba(255, 255, 255, 0.04);
  backdrop-filter: blur(15px);
  border-radius: 1rem;
  padding: 6px 10px;
}

.speed-display {
  display: flex;
  align-items: center;
  color: var(--color-white-40);

  &.active {
    color: #ffffff;
  }
}

.speed-value {
  font-size: 28px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  transition: color 0.5s ease;

  &.speed-ok {
    color: oklch(0.72 0.1 145);
  }

  &.speed-low {
    color: oklch(0.68 0.1 25);
  }
}

// --- Timer info (game mode) ---

.walk-timer-info {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
}

.timer-row {
  display: flex;
  align-items: center;
  gap: 6px;
  color: var(--color-white-40);
  font-size: 13px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  transition: color 0.3s ease;

  &.active {
    color: var(--color-white);
  }
}

.timer-label {
  min-width: 42px;
}

.timer-value {
  font-variant-numeric: tabular-nums;
}

.turn-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  transition:
    background 0.2s ease,
    transform 0.2s ease;
  flex-shrink: 0;

  &.white {
    background: var(--color-white);
    box-shadow: 0 0 6px var(--color-white-50);
    transform: scale(1.2);
  }

  &.black {
    background: #000;
    border: 1px solid var(--color-white-30);
    transform: scale(1.2);
  }

  &.pink {
    background: var(--color-pink);
    box-shadow: 0 0 6px var(--color-pink);
    transform: scale(1.2);
  }

  &.inactive {
    background: var(--color-white-30);
  }
}

// --- Settings header (settings mode) ---

.zone-fade-enter-active,
.zone-fade-leave-active {
  transition: opacity 0.25s ease;
}

.zone-fade-enter-from,
.zone-fade-leave-to {
  opacity: 0;
}

// The gap between the viewport edge and the board's, so the close button frames
// the board instead of the window. Same expression DuelView uses, and the same
// one .zone-board is sized by. It tracks the board on vertical resize too, since
// the board's width is a min() that includes 100dvh.
$walk-board-edge: max(12px, calc(50% - min(100vw - 20px, 100dvh - var(--board-offset, 320px), 560px) / 2));

.walk-close-btn {
  position: absolute;
  z-index: 1000;
  bottom: calc(var(--safe-bottom-offset) + var(--screen-nav-label-offset));
  right: $walk-board-edge;
  width: var(--nav-cell-size, 3rem);
  height: var(--nav-cell-size, 3rem);
  border-radius: 50%;
  border: 1px solid var(--color-white-10);
  backdrop-filter: blur(8px);
  background-color: var(--color-dark-5);
  color: var(--color-white-70);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  touch-action: manipulation;
  transition:
    background-color 200ms linear,
    border-color 200ms linear,
    color 200ms linear,
    opacity 200ms linear,
    transform 200ms linear;

  @include hover {
    background-color: var(--color-dark-20);
    border-color: var(--color-white-30);
    color: var(--color-white);
  }

  @include press;

  &.disabled {
    opacity: 0.3;
    pointer-events: none;
  }
}

.walk-map-btn {
  right: auto;
  left: $walk-board-edge;
  padding: 0;
}

// A chessboard square with a piece on it. Scaled to a third of the cell, as it
// was against the smaller button it used to sit in.
.toolbar-squares-icon {
  width: 16px;
  height: 16px;
  border-radius: 2px;
  border: 1px solid currentColor;
  display: flex;
  align-items: center;
  justify-content: center;
}

.toolbar-squares-dot {
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: oklch(0.72 0.17 145);
}

// --- Toolbar CTAs ---

.walk-toolbar-fixed {
  position: fixed;
  top: calc(var(--sai-top) + 12px);
  left: 0;
  right: 0;
  z-index: 600;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  pointer-events: none;

  > * {
    pointer-events: auto;
  }
}

.walk-toolbar {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  transition: gap 200ms ease;

  &.editing {
    gap: 0;
  }
}

.toolbar-cta-wrapper {
  display: flex;
  align-items: center;
  gap: 6px;
  transition:
    gap 200ms ease,
    width 200ms ease,
    opacity 200ms ease;
  overflow: hidden;

  &.expanded {
    gap: 8px;
    flex-wrap: wrap;
    justify-content: center;
  }

  &.collapsed {
    width: 0;
    opacity: 0;
    gap: 0;
    pointer-events: none;
  }
}

.toolbar-cta-label {
  width: 100%;
  text-align: center;
  font-size: 9px;
  font-weight: 500;
  color: var(--color-white-40);
  margin-top: -4px;
  letter-spacing: 0.02em;
}

.toolbar-labels {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin-top: -4px;
}

.toolbar-label {
  width: 44px;
  text-align: center;
  font-size: 9px;
  font-weight: 500;
  color: var(--color-white-40);
  letter-spacing: 0.02em;
}

.toolbar-cta {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  border: none;
  background: rgba(255, 255, 255, 0.06);
  color: var(--color-white-70);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition:
    all 150ms ease,
    width 200ms ease,
    opacity 200ms ease;
  position: relative;
  flex-shrink: 0;
  padding: 0;
  overflow: hidden;

  &.collapsed {
    width: 0;
    height: 44px;
    opacity: 0;
    border: none;
    padding: 0;
    margin: 0;
    pointer-events: none;
  }

  @include hover {
    background: rgba(255, 255, 255, 0.12);
    color: var(--color-white);
  }

  &.active {
    background: rgba(255, 255, 255, 0.15);
    color: var(--color-white);
  }

  &.toolbar-cta-toggle:not(.is-off) {
    color: var(--color-white);
  }

  // Same disc as the other filters. On, the square's own border picks up the pink
  // the inspect mode used to paint on the nav button.
  &.toolbar-cta-toggle.is-squares-on {
    color: var(--color-pink);
  }

  &.toolbar-cta-toggle.is-off {
    color: var(--color-white-30);
    background: rgba(255, 255, 255, 0.02);

    .tid {
      background: rgba(255, 255, 255, 0.15) !important;
      border-color: rgba(255, 255, 255, 0.15) !important;
    }
  }

  // No press: these toggle `active` / `is-off`, so the colour change already
  // reports the tap. A scale on top of it reads as pressed *and* selected.
}

.toolbar-cta-text {
  font-size: 12px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  color: var(--color-white);
}

.toolbar-cta-unit {
  font-size: 9px;
  font-weight: 500;
  color: var(--color-white-50);
  margin-left: 1px;
}

.toolbar-expand-btn {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 1px solid var(--color-white-10);
  background: rgba(255, 255, 255, 0.06);
  color: var(--color-white);
  font-size: 18px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 150ms ease;
  padding: 0;
  @include hover-capable {
    &:hover:not(:disabled) {
      background: rgba(255, 255, 255, 0.12);
      border-color: var(--color-white-30);
    }
  }

  &:disabled {
    opacity: 0.25;
    cursor: not-allowed;
  }
}

.toolbar-strike {
  position: absolute;
  inset: 0;
  pointer-events: none;

  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 15%;
    width: 70%;
    height: 2px;
    background: var(--color-white-50);
    transform: rotate(-45deg);
    border-radius: 1px;
  }
}

.toolbar-trace-badge {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 6px;
  height: 6px;
  border-radius: 1.5px;
  background-color: oklch(0.7818 0.2418 355);
  pointer-events: none;

  .is-off & {
    background-color: var(--color-white-30);
  }
}

.toolbar-route-icon {
  transform: none;
}

.toolbar-icon-dots {
  display: grid;
  gap: 2px;

  .tid {
    width: 7px;
    height: 7px;
    border-radius: 50%;
  }
}

.toolbar-icon-progress {
  grid-template-columns: 1fr 1fr;

  .tid.green {
    background: oklch(0.72 0.17 145);
  }

  .tid.red {
    background: oklch(0.65 0.2 25);
  }

  .tid.hollow {
    background: transparent;
    border: 1.5px solid rgba(255, 255, 255, 0.35);
  }
}

.toolbar-icon-info {
  grid-template-columns: 1fr 1fr;
  grid-template-rows: 1fr 1fr;

  .tid.black {
    background: #000;
  }

  .tid.white {
    background: var(--color-white);
  }

  .tid.pink {
    background: var(--color-pink);
    grid-column: 1 / -1;
    justify-self: center;
  }
}

.walk-game-info {
  position: fixed;
  top: calc(var(--sai-top) + 12px);
  left: 0;
  right: 0;
  z-index: 600;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--spacing-l);
  pointer-events: none;
}

// --- Action CTA ---

.walk-action-row {
  display: flex;
  align-items: center;
  gap: 12px;
  position: relative;
}

// Spans the viewport behind the CTA row, with the circles pinned to the edges.
// Offset by half a circle so the road lands on the CTA's centre line whatever
// height `space-evenly` gives the column.
.walk-route-layer {
  position: absolute;
  top: calc(50% - 1.5rem);
  left: 50%;
  transform: translateX(-50%);
  width: 100vw;
  padding-left: 2.5rem;
  padding-right: 2.5rem;
  display: flex;
  align-items: flex-start;
  justify-content: center;

  // Frozen mid-walk; in setup the circles stay tappable for picking a spot.
  &.is-static {
    pointer-events: none;
  }
}

.walk-start-zoom-row {
  gap: 10px;
}

.walk-zoom-glyph {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 46px;
  height: 46px;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  color: #ffffff;
  touch-action: manipulation;
  transition: opacity 150ms ease;

  &.disabled,
  &:disabled {
    opacity: 0.3;
    pointer-events: none;
  }

  &:active:not(:disabled) {
    transform: scale(0.92);
  }
}

.walk-zoom-glyph-icon {
  display: block;
  font-family: 'Hanken Grotesk', sans-serif;
  font-weight: 900;
  font-size: 56px;
  line-height: 1;
  color: currentColor;
  opacity: 0.35;
  user-select: none;
  -webkit-text-stroke: 1.7px currentColor;
  /* +/− are drawn on the font's math axis, which sits below the line-box
     centre, so box centring alone leaves them low against the Start label. */
  transform: translateY(-0.06em);
}

.walk-action-cta {
  width: 76px;
  height: 76px;
  border-radius: 50%;
  border: none;
  padding: 0;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition: all 200ms ease;
  position: relative;
  z-index: 1;

  &.disabled {
    opacity: 0.3;
    pointer-events: none;
  }

  @include press;
}

.walk-action-start {
  // Opaque base under the tint, otherwise the route's dashes read straight
  // through the pink and the road looks like it crosses the button.
  background: linear-gradient(var(--color-pink-30), var(--color-pink-30)), var(--color-dark-700);
  color: var(--color-white);

  // Dimmed by colour rather than opacity, which would let the road back through.
  &.disabled {
    opacity: 1;
    background: linear-gradient(var(--color-pink-10), var(--color-pink-10)), var(--color-dark-700);
    color: var(--color-white-30);
  }
}

.walk-action-reveal {
  background: rgba(255, 255, 255, 0.15);
  color: var(--color-white);
  backdrop-filter: blur(15px);

  @include hover {
    background: rgba(255, 255, 255, 0.22);
  }
}

.walk-force-quit {
  width: 54px;
  height: 54px;
  font-size: 10px;
  line-height: 1.2;
  background: rgba(255, 255, 255, 0.08);
  color: var(--color-white-70);

  @include hover {
    background: rgba(255, 255, 255, 0.15);
    color: var(--color-white);
  }
}

.reveal-loading-overlay {
  position: absolute;
  inset: 0;
  z-index: 10;
  background: rgba(0, 0, 0, 0.3);
  border-radius: var(--radius-l);
  pointer-events: all;
  display: flex;
  align-items: center;
  justify-content: center;
}

.reveal-spinner {
  width: 32px;
  height: 32px;
  border: 3px solid rgba(255, 255, 255, 0.2);
  border-top-color: rgba(255, 255, 255, 0.8);
  border-radius: 50%;
  animation: reveal-spin 0.6s linear infinite;
}

@keyframes reveal-spin {
  to {
    transform: rotate(360deg);
  }
}

.spot-connector {
  flex: 1;
  height: 0;
  border-top: 1px dashed var(--color-white-25);
  margin-top: calc(3rem / 2);
}
</style>
